import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface PressRelease {
  id: number;
  title: string;
  content: string;
  thumbnail_url: string | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  author: string;
  department: string;
  view_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface PressReleaseContextType {
  releases: PressRelease[];
  loading: boolean;
  addRelease: (release: Omit<PressRelease, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateRelease: (id: number, release: Partial<PressRelease>) => Promise<void>;
  deleteRelease: (id: number) => Promise<void>;
  getRelease: (id: number) => PressRelease | undefined;
  incrementViewCount: (id: number) => Promise<void>;
  refreshReleases: () => Promise<void>;
  ensureLoaded: () => void;
}

const PressReleaseContext = createContext<PressReleaseContextType | undefined>(undefined);

export const PressReleaseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [releases, setReleases] = useState<PressRelease[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('press_releases')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReleases(data || []);
    } catch (error) {
      console.error('Error fetching press releases:', error);
    } finally {
      setLoading(false);
    }
  };

  // 지연 로드: 앱 부팅 시 모든 Provider가 전체 테이블을 읽으면 방문자 1명당 요청 수십 건이 발생해
  // 접속이 몰릴 때 DB가 포화된다. 이 데이터를 쓰는 화면이 뜰 때만 한 번 읽는다.
  const loadedRef = useRef(false);
  const ensureLoaded = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchReleases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRelease = async (release: Omit<PressRelease, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await supabase
        .from('press_releases')
        .insert({
          ...release,
          view_count: 0
        });

      if (error) throw error;
      await fetchReleases();
    } catch (error) {
      console.error('Error adding press release:', error);
      throw error;
    }
  };

  const updateRelease = async (id: number, release: Partial<PressRelease>) => {
    try {
      const { error } = await supabase
        .from('press_releases')
        .update({ ...release, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      await fetchReleases();
    } catch (error) {
      console.error('Error updating press release:', error);
      throw error;
    }
  };

  const deleteRelease = async (id: number) => {
    try {
      const { error } = await supabase
        .from('press_releases')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setReleases(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error deleting press release:', error);
      throw error;
    }
  };

  const getRelease = (id: number) => {
    return releases.find(r => r.id === id);
  };

  const incrementViewCount = async (id: number) => {
    try {
      const release = releases.find(r => r.id === id);
      if (!release) return;

      await supabase
        .from('press_releases')
        .update({ view_count: release.view_count + 1 })
        .eq('id', id);

      setReleases(prev =>
        prev.map(r => r.id === id ? { ...r, view_count: r.view_count + 1 } : r)
      );
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const refreshReleases = async () => {
    await fetchReleases();
  };

  return (
    <PressReleaseContext.Provider value={{
      ensureLoaded,
      releases,
      loading,
      addRelease,
      updateRelease,
      deleteRelease,
      getRelease,
      incrementViewCount,
      refreshReleases
    }}>
      {children}
    </PressReleaseContext.Provider>
  );
};

export const usePressRelease = () => {
  const context = useContext(PressReleaseContext);
  // 이 훅을 쓰는 화면이 실제로 떴을 때만 조회를 시작한다(지연 로드).
  const ensureLoaded = context?.ensureLoaded;
  useEffect(() => {
    ensureLoaded?.();
  }, [ensureLoaded]);

  if (context === undefined) {
    throw new Error('usePressRelease must be used within a PressReleaseProvider');
  }
  return context;
};
